import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { useBackend } from "../../lib/backend";
import { PsychAssistSetupFlow } from "../psychassist/PsychAssistSetupFlow";
import { TourCard } from "./TourCard";
import {
  CURRENT_VERSION,
  LAST_VERSION_KEY,
  TOUR_STORAGE_KEY,
  getTourSteps,
} from "./tour-content";
import type { SurfaceCapabilities } from "../../lib/surface-capabilities";
import { shouldStartDesktopOnboarding } from "./desktop-onboarding-policy";

export function FirstTimeTour({
  capabilities,
  onNavigate,
}: {
  capabilities?: SurfaceCapabilities;
  onNavigate?: (tab: string) => void;
}) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [opacity, setOpacity] = useState(0);
  const measureTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const activeTargetRef = useRef<Element | null>(null);
  const onNavigateRef = useRef(onNavigate);
  const { addToast } = useToast();
  const { isAuthenticated, isLoading, user, userId } = useAuth();
  const backend = useBackend();
  const userCacheKey = getUserTourCacheKey(userId || user?.email);
  const tourSteps = useMemo(() => getTourSteps(capabilities), [capabilities]);
  const isElectronDesktop = capabilities?.isElectronDesktop === true;
  const suppressAutoTour = true;

  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  const startTour = useCallback(() => {
    const firstStep = tourSteps[0];
    if (!firstStep) return;
    setActive(true);
    if (firstStep.nav) onNavigateRef.current?.(firstStep.nav);
    requestAnimationFrame(() => setOpacity(1));
  }, [tourSteps]);

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener("fintheon:start-first-time-tour", handler);
    return () =>
      window.removeEventListener("fintheon:start-first-time-tour", handler);
  }, [startTour]);

  useEffect(() => {
    if (suppressAutoTour) return;
    if (!isElectronDesktop) return;
    if (isLoading) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleStart = () => {
      timer = setTimeout(() => {
        if (!cancelled) startTour();
      }, 1000);
    };

    async function hydrateTourState() {
      if (!isAuthenticated || readTourCache(userCacheKey)) return;

      try {
        const profile = await backend.profile.get();
        const appState = await backend.profile.getAppState().catch(() => ({}));
        if (cancelled) return;

        const hasCompletedRemotely = hasRemoteTourCompleted(appState);
        if (
          !shouldStartDesktopOnboarding({
            hasCompletedLocally: readTourCache(userCacheKey),
            hasCompletedRemotely,
            isAuthenticated,
            isElectronDesktop,
            profileOnboardingComplete: profile.onboarding_complete,
          })
        ) {
          if (profile.onboarding_complete || hasCompletedRemotely) {
            writeTourCache(userCacheKey);
          }
          return;
        }

        scheduleStart();
      } catch (error) {
        console.warn("[Onboarding] Failed to hydrate tour state:", error);
      }
    }

    void hydrateTourState();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    backend.profile,
    isAuthenticated,
    isElectronDesktop,
    isLoading,
    startTour,
    userCacheKey,
  ]);

  const clearActiveTarget = useCallback(() => {
    activeTargetRef.current?.removeAttribute(
      "data-fintheon-tour-target-active",
    );
    activeTargetRef.current = null;
  }, []);

  const measureTarget = useCallback(
    (attempt = 0) => {
      const current = tourSteps[step];
      if (!current) return;
      clearActiveTarget();
      const el = document.querySelector(current.selector);
      setTargetRect(el ? el.getBoundingClientRect() : null);
      if (el) {
        el.setAttribute("data-fintheon-tour-target-active", "true");
        activeTargetRef.current = el;
        return;
      }

      if (attempt < 8) {
        clearTimeout(measureTimer.current);
        measureTimer.current = setTimeout(
          () => measureTarget(attempt + 1),
          120,
        );
      }
    },
    [clearActiveTarget, step, tourSteps],
  );

  useEffect(() => {
    if (!active) return;
    const current = tourSteps[step];
    if (!current) return;

    if (current.nav) onNavigateRef.current?.(current.nav);

    clearTimeout(measureTimer.current);
    measureTimer.current = setTimeout(() => measureTarget(), 260);

    return () => {
      clearTimeout(measureTimer.current);
      clearActiveTarget();
    };
  }, [active, clearActiveTarget, measureTarget, step, tourSteps]);

  useEffect(() => {
    if (step >= tourSteps.length) setStep(Math.max(0, tourSteps.length - 1));
  }, [step, tourSteps.length]);

  useEffect(() => {
    if (!active) return;
    const handleResize = () => measureTarget();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [active, measureTarget]);

  const completeTour = useCallback(() => {
    setOpacity(0);
    clearActiveTarget();
    setTimeout(() => {
      writeTourCache(userCacheKey);
      localStorage.setItem(LAST_VERSION_KEY, CURRENT_VERSION);
      setActive(false);
      onNavigateRef.current?.("dashboard");
      if (isAuthenticated) {
        void persistTourCompletion({
          backend,
          completedAt: new Date().toISOString(),
        });
      }
      addToast(
        "Welcome to Fintheon",
        "success",
        "Your tour is complete. Explore at your own pace.",
      );
    }, 300);
  }, [addToast, backend, clearActiveTarget, isAuthenticated, userCacheKey]);

  const goNext = useCallback(() => {
    if (step < tourSteps.length - 1) setStep(step + 1);
    else completeTour();
  }, [step, completeTour, tourSteps.length]);

  const goPrev = useCallback(() => {
    if (step > 0) setStep(step - 1);
  }, [step]);

  useEffect(() => {
    if (!active) return;
    const current = tourSteps[step];
    if (!current?.advanceOnTargetClick) return;
    const handlePointerUp = (event: PointerEvent) => {
      const target = event.target;
      const el = document.querySelector(current.selector);
      if (!(target instanceof Node) || !el?.contains(target)) return;
      window.setTimeout(goNext, 120);
    };
    document.addEventListener("pointerup", handlePointerUp, true);
    return () =>
      document.removeEventListener("pointerup", handlePointerUp, true);
  }, [active, goNext, step, tourSteps]);

  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") completeTour();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active, completeTour, goNext, goPrev]);

  if (!active || tourSteps.length === 0) return <PsychAssistSetupFlow />;

  return (
    <>
      <PsychAssistSetupFlow />

      <div
        className="fintheon-tour-overlay fixed inset-0 z-[9998]"
        style={{
          opacity,
        }}
      />

      <TourCard
        step={tourSteps[step]}
        stepIndex={step}
        totalSteps={tourSteps.length}
        targetRect={targetRect}
        onNext={goNext}
        onPrev={goPrev}
        onSkip={completeTour}
      />
    </>
  );
}

function getUserTourCacheKey(userKey?: string | null): string {
  if (!userKey) return TOUR_STORAGE_KEY;
  return `${TOUR_STORAGE_KEY}:${userKey.trim().toLowerCase()}`;
}

function readTourCache(cacheKey: string): boolean {
  try {
    return localStorage.getItem(cacheKey) === "true";
  } catch {
    return false;
  }
}

function writeTourCache(cacheKey: string): void {
  try {
    localStorage.setItem(cacheKey, "true");
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  } catch {
    /* localStorage may be unavailable */
  }
}

function hasRemoteTourCompleted(appState: Record<string, unknown>): boolean {
  const onboarding = appState.onboarding;
  if (!onboarding || typeof onboarding !== "object") return false;
  const state = onboarding as { tourCompleted?: unknown; completed?: unknown };
  return state.tourCompleted === true || state.completed === true;
}

async function persistTourCompletion({
  backend,
  completedAt,
}: {
  backend: ReturnType<typeof useBackend>;
  completedAt: string;
}): Promise<void> {
  try {
    await backend.profile.get().catch(() => null);
    await backend.profile.update({ onboarding_complete: true });
    await backend.profile.updateAppState({
      onboarding: {
        tourCompleted: true,
        tourVersion: CURRENT_VERSION,
        completedAt,
      },
    });
  } catch (error) {
    console.warn("[Onboarding] Failed to persist tour completion:", error);
  }
}
